import { useState, useEffect, useCallback } from 'react';
import { X, Save, Loader2, ExternalLink, History, CreditCard, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { getUserFullDetails, updateUserInfo, UserDetails, canManageUser, getUserAuditLogs, getStripeCustomerUrl, getStripeSubscriptionUrl } from '../lib/admin';

type AuditLogRecord = {
  id: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  table_name: string;
  created_at: string;
  new_data?: Record<string, unknown> | null;
};

interface UserDetailsModalProps {
  userId: string;
  onClose: () => void;
  onUserUpdated: () => void;
}

export function UserDetailsModal({ userId, onClose, onUserUpdated }: UserDetailsModalProps) {
  const [user, setUser] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLogRecord[]>([]);
  const [editedData, setEditedData] = useState<{
    businessName: string;
    contactName: string;
    phone: string;
    address: string;
    email: string;
  }>({
    businessName: '',
    contactName: '',
    phone: '',
    address: '',
    email: ''
  });

  const loadUserDetails = useCallback(async () => {
    try {
      const data = await getUserFullDetails(userId);
      if (data) {
        setUser(data);
        setEditedData({
          businessName: data.business_name,
          contactName: data.contact_name,
          phone: data.phone,
          address: data.address,
          email: data.email
        });
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      toast.error('Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    let mounted = true;

    Promise.allSettled([
      getUserFullDetails(userId),
      canManageUser(userId),
    ])
      .then(([userResult, canManageResult]) => {
        if (!mounted) return;

        if (userResult.status === 'fulfilled' && userResult.value) {
          const data = userResult.value;
          setUser(data);
          setEditedData({
            businessName: data.business_name,
            contactName: data.contact_name,
            phone: data.phone,
            address: data.address,
            email: data.email
          });
        } else if (userResult.status === 'rejected') {
          console.error('Error loading user details:', userResult.reason);
          toast.error('Failed to load user details');
        }

        if (canManageResult.status === 'fulfilled') {
          setCanEdit(canManageResult.value);
        } else {
          console.error('Error checking permissions:', canManageResult.reason);
          setCanEdit(false);
        }
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const loadAuditLogs = async () => {
    try {
      const logs = await getUserAuditLogs(userId);
      setAuditLogs(logs as AuditLogRecord[]);
      setShowAuditLog(true);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Failed to load audit logs');
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setIsSaving(true);
      await updateUserInfo(userId, {
        businessName: editedData.businessName,
        contactName: editedData.contactName,
        phone: editedData.phone,
        address: editedData.address,
        email: editedData.email
      });

      toast.success('User information updated successfully');
      setIsEditing(false);
      await loadUserDetails();
      onUserUpdated();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!user) return;
    setEditedData({
      businessName: user.business_name,
      contactName: user.contact_name,
      phone: user.phone,
      address: user.address,
      email: user.email
    });
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-400 bg-green-500/10';
      case 'pending':
        return 'text-yellow-400 bg-yellow-500/10';
      case 'canceled':
        return 'text-red-400 bg-red-500/10';
      default:
        return 'text-gray-400 bg-gray-500/10';
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-scale-navy rounded-lg p-8 max-w-4xl w-full mx-4">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-scale-blue animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-scale-navy rounded-lg p-8 max-w-4xl w-full mx-4">
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <p className="text-white">User not found</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-white transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-scale-navy rounded-lg shadow-xl max-w-4xl w-full my-8 border border-gray-800">
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">User Details</h2>
            <div className="flex items-center space-x-2">
              {canEdit && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-scale-blue text-scale-dark rounded-md hover:bg-white transition-colors"
                >
                  Edit User
                </button>
              )}
              <button
                onClick={loadAuditLogs}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="View audit logs"
              >
                <History className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={editedData.email}
                    onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white"
                  />
                ) : (
                  <p className="text-white">{user.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Business Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedData.businessName}
                    onChange={(e) => setEditedData({ ...editedData, businessName: e.target.value })}
                    className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white"
                  />
                ) : (
                  <p className="text-white">{user.business_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Contact Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedData.contactName}
                    onChange={(e) => setEditedData({ ...editedData, contactName: e.target.value })}
                    className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white"
                  />
                ) : (
                  <p className="text-white">{user.contact_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Phone</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editedData.phone}
                    onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white"
                  />
                ) : (
                  <p className="text-white">{user.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Address</label>
                {isEditing ? (
                  <textarea
                    value={editedData.address}
                    onChange={(e) => setEditedData({ ...editedData, address: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white"
                  />
                ) : (
                  <p className="text-white">{user.address}</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Account Information</h3>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Account Status</label>
                <span className={`inline-block px-3 py-1 text-sm rounded-full ${getSubscriptionStatusColor(user.subscription_status || 'none')}`}>
                  {user.subscription_status || 'No Subscription'}
                </span>
              </div>

              {user.subscription_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Subscription ID</label>
                  <div className="flex items-center space-x-2">
                    <p className="text-white text-sm font-mono">{user.subscription_id.substring(0, 20)}...</p>
                    <a
                      href={getStripeSubscriptionUrl(user.subscription_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-scale-blue hover:text-white transition-colors"
                      title="View in Stripe"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              {user.customer_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Customer ID</label>
                  <div className="flex items-center space-x-2">
                    <p className="text-white text-sm font-mono">{user.customer_id.substring(0, 20)}...</p>
                    <a
                      href={getStripeCustomerUrl(user.customer_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-scale-blue hover:text-white transition-colors"
                      title="View in Stripe"
                    >
                      <CreditCard className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Onboarding Progress</label>
                <p className="text-white">Step {user.onboarding_step} of 8</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Account Created</label>
                <p className="text-white">{formatDate(user.created_at)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Last Updated</label>
                <p className="text-white">{formatDate(user.updated_at)}</p>
              </div>
            </div>
          </div>

          {user.service_types && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Service Selections</h3>
              <div className="bg-scale-darker p-4 rounded-lg">
                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Selected Services</label>
                    <p className="text-white">{Array.isArray(user.service_types) ? user.service_types.join(', ') : 'None'}</p>
                  </div>
                  {user.selected_tier && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400">Selected Tier</label>
                      <p className="text-white">{typeof user.selected_tier === 'object' ? user.selected_tier.name : user.selected_tier}</p>
                    </div>
                  )}
                  {user.selected_addons && Array.isArray(user.selected_addons) && user.selected_addons.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-400">Add-ons</label>
                      <p className="text-white">{user.selected_addons.join(', ')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {user.automation_requirements && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Automation Requirements</h3>
              <div className="bg-scale-darker p-4 rounded-lg">
                <p className="text-white whitespace-pre-wrap">{user.automation_requirements}</p>
              </div>
            </div>
          )}

          {user.custom_ai_requirements && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Custom AI Requirements</h3>
              <div className="bg-scale-darker p-4 rounded-lg">
                <p className="text-white whitespace-pre-wrap">{user.custom_ai_requirements}</p>
              </div>
            </div>
          )}
        </div>

        {isEditing && (
          <div className="p-6 border-t border-gray-800 flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-4 py-2 text-white bg-gray-700 rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center px-4 py-2 bg-scale-blue text-scale-dark rounded hover:bg-white transition-colors"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}

        {showAuditLog && (
          <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-scale-navy rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-scale-navy">
                <h3 className="text-xl font-bold text-white">Audit Log</h3>
                <button
                  onClick={() => setShowAuditLog(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                {auditLogs.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No audit logs found</p>
                ) : (
                  <div className="space-y-4">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="bg-scale-darker p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            log.action === 'INSERT' ? 'bg-green-500/10 text-green-400' :
                            log.action === 'UPDATE' ? 'bg-blue-500/10 text-blue-400' :
                            'bg-red-500/10 text-red-400'
                          }`}>
                            {log.action}
                          </span>
                          <span className="text-sm text-gray-400">{formatDate(log.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">Table: {log.table_name}</p>
                        {log.new_data && (
                          <div className="text-sm text-white">
                            <p className="font-medium mb-1">Changes:</p>
                            <pre className="text-xs bg-scale-dark p-2 rounded overflow-x-auto">
                              {JSON.stringify(log.new_data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
